import { Input, type InputProps } from '@fluentui/react-components';
import { forwardRef, useCallback, useState } from 'react';

function parseNumber(value: string): number | null {
    // check for leading minus
    const isNegative = value.trim().startsWith('-');

    // remove everything that is not a number or a comma
    value = value.replace(/[^0-9,]/g, '');

    // empty is null
    if (!value) {
        return null;
    }

    // keep only the last comma
    const lastCommaIndex = value.lastIndexOf(',');
    if (lastCommaIndex !== -1) {
        value = value.substring(0, lastCommaIndex).replace(/,/g, '') + value.substring(lastCommaIndex);
    }

    // only comma left is null
    if (value === ',') {
        return null;
    }

    // replace comma with dot
    value = value.replace(',', '.');

    // parse number
    return parseFloat(value) * (isNegative ? -1 : 1);
}

function formatNumber(value: number | null, integer: boolean, suffix?: string): string {
    if (value === null) {
        return '';
    }
    if (integer) {
        return Math.round(value).toString() + (suffix ?? '');
    }
    return value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + (suffix ?? '');
}

export type NumberInputProps = Omit<InputProps, 'value' | 'onChange'> & {
    value: number | null;
    onChange: (value: number | null) => void;
    integer?: boolean;
    suffix?: string;
};

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
    ({ value, onChange, integer = false, suffix, style, ...props }, ref) => {
        const [textOverride, setTextOverride] = useState<[number | null, string] | null>(null);

        const applyEdit = useCallback(
            (textValue: string, final: boolean) => {
                const parsedValue = parseNumber(textValue);
                setTextOverride(!final ? [parsedValue, textValue] : null);
                onChange(parsedValue);
            },
            [onChange]
        );

        return (
            <Input
                {...props}
                ref={ref}
                style={{ minWidth: 0, ...style }}
                value={
                    textOverride && textOverride[0] === value ? textOverride[1] : formatNumber(value, integer, suffix)
                }
                onChange={(e) => {
                    applyEdit(e.target.value, false);
                }}
                onBlur={(e) => {
                    applyEdit(e.target.value, true);
                }}
            />
        );
    }
);
